import { localStorageAvaiable, sessionStorageAvaiable } from './check.js';
import { getTimestamp } from './time.js';
const TIME_SUFFIX = '--time';
export var StorageType;
(function (StorageType) {
    StorageType[StorageType["LocalStorage"] = 0] = "LocalStorage";
    StorageType[StorageType["SessionStorage"] = 1] = "SessionStorage";
})(StorageType = StorageType || (StorageType = {}));
class Storage {
    constructor(type = StorageType.LocalStorage) {
        this._type = StorageType.LocalStorage;
        this._storageAvailable = null;
        this._type = type;
    }
    get adapter() {
        return this.storageAvailable
            ? this._type == StorageType.LocalStorage
                ? window.localStorage
                : window.sessionStorage
            : null;
    }
    get storageAvailable() {
        if (this._storageAvailable === null) {
            this._storageAvailable =
                this._type == StorageType.LocalStorage ? localStorageAvaiable() : sessionStorageAvaiable();
        }
        return this._storageAvailable;
    }
    /**
     * 构造客户端存储接口参数
     * @param params
     * @returns
     */
    buildStorageParams(params) {
        return Object.assign({
            cache: this._type !== StorageType.LocalStorage
        }, params);
    }
    /**
     * 设置缓存
     * @param key
     * @param value
     */
    setItem(key, value, options = {}) {
        if (!this.storageAvailable) {
            return false;
        }
        const adapter = this.adapter;
        adapter.setItem(key, JSON.stringify(value));
        const timeKey = key + TIME_SUFFIX;
        if (options && 'updateTime' in options && !options.updateTime) {
            const latestTime = adapter.getItem(timeKey);
            if (latestTime) {
                return true;
            }
        }
        adapter.setItem(timeKey, getTimestamp() + '');
        return true;
    }
    /**
     * 设置缓存，优先使用客户端存储
     * @param key
     * @param value
     */
    setItemAsync(key, value) {
        return new Promise((resolve) => {
            setTimeout(() => {
                const res = this.setItem(key, value);
                resolve(res);
            }, 10);
        });
    }
    /**
     * 获取缓存
     * @param key
     * @param defaultValue
     * @param resetTime
     */
    getItem(key, defaultValue = null, resetTime = 0) {
        if (!this.storageAvailable) {
            return defaultValue;
        }
        const adapter = this.adapter;
        let t = adapter.getItem(key);
        if (t !== null && resetTime > 0) {
            const cTime = getTimestamp();
            let cacheTime = adapter.getItem(key + TIME_SUFFIX);
            cacheTime = !cacheTime ? 0 : parseInt(cacheTime);
            if (cTime - cacheTime > resetTime) {
                t = null;
                this.removeItem(key);
            }
        }
        if (t === null) {
            return defaultValue;
        }
        else {
            return JSON.parse(t);
        }
    }
    /**
     * 获取缓存，优先使用客户端存储
     * @param key
     * @param defaultValue
     * @param resetTime
     */
    getItemAsync(key, defaultValue = null, resetTime = 0) {
        return new Promise((resolve) => {
            setTimeout(() => {
                const res = this.getItem(key, defaultValue, resetTime);
                resolve(res);
            }, 10);
        });
    }
    /**
     * 移除缓存
     * @param key
     */
    removeItem(key) {
        if (!this.storageAvailable) {
            return false;
        }
        const adapter = this.adapter;
        adapter.removeItem(key);
        adapter.removeItem(key + TIME_SUFFIX);
        return true;
    }
    /**
     * 移除缓存，优先使用客户端存储
     * @param key
     */
    removeItemAsync(key) {
        return new Promise((resolve) => {
            setTimeout(() => {
                const res = this.removeItem(key);
                resolve(res);
            }, 10);
        });
    }
    /**
     * 清空缓存
     */
    clear() {
        if (!this.storageAvailable) {
            return false;
        }
        const adapter = this.adapter;
        adapter.clear();
        return true;
    }
    /**
     * 清空缓存
     */
    clearAsync() {
        return new Promise((resolve) => {
            setTimeout(() => {
                const res = this.clear();
                resolve(res);
            }, 10);
        });
    }
    /**
     * 获取存储信息
     * @returns
     */
    getStorageInfo() {
        return new Promise((resolve) => {
            const keys = [];
            const adapter = this.adapter;
            for (let i = adapter.length - 1; i >= 0; i--) {
                keys.push(adapter.key(i));
            }
            setTimeout(() => {
                resolve({
                    keys
                });
            }, 10);
        });
    }
}
let instanceMap = {};
export function getStorageInstance(type) {
    if (!instanceMap[type]) {
        instanceMap[type] = new Storage(type);
    }
    return instanceMap[type];
}
export function getLocalStorage() {
    return getStorageInstance(StorageType.LocalStorage);
}
export function getSessionStorage() {
    return getStorageInstance(StorageType.SessionStorage);
}
